import React, { useState, useEffect } from 'react';
import { collection, addDoc, onSnapshot, doc, getDocs, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import './AddAccountDialog.css';

function AddAccountDialog({ isOpen, onClose, onSuccess, editAccount = null }) {
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [allowExpense, setAllowExpense] = useState(false);
  const [allowIncome, setAllowIncome] = useState(false);
  const [allowTransfer, setAllowTransfer] = useState(false);
  const [balance, setBalance] = useState('0.00');
  const [group, setGroup] = useState('');
  const [cutoffDate, setCutoffDate] = useState('');
  const [creditLimit, setCreditLimit] = useState('');
  const [files, setFiles] = useState(false);
  const [ledger, setLedger] = useState('');
  
  const [accountGroups, setAccountGroups] = useState([]);
  const [ledgerOptions, setLedgerOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = auth.currentUser;

  const isEditMode = editAccount !== null;

  // Get selected group's type
  const selectedGroup = accountGroups.find(g => g.name === group);
  const isLiabilityGroup = selectedGroup?.type === 'Liability';

  // Reset ledger when group changes
  useEffect(() => {
    setLedger('');
  }, [group]);

  // Populate form when editing
  useEffect(() => {
    if (editAccount) {
      setName(editAccount.name || '');
      setActive(editAccount.active ?? true);
      setAllowExpense(editAccount.allowExpense || false);
      setAllowIncome(editAccount.allowIncome || false);
      setAllowTransfer(editAccount.allowTransfer || false);
      setGroup(editAccount.group || '');
      setLedger(editAccount.Ledger || '');
      setFiles(editAccount.files || false);
      setCutoffDate(editAccount.cutoff_date || '');
      setCreditLimit(editAccount.credit_limit?.toString() || '');
      // Balance is not editable in edit mode, so we don't set it
    } else {
      // Reset form when not editing
      setName('');
      setActive(true);
      setAllowExpense(false);
      setAllowIncome(false);
      setAllowTransfer(false);
      setBalance('0.00');
      setGroup('');
      setCutoffDate('');
      setCreditLimit('');
      setFiles(false);
      setLedger('');
    }
  }, [editAccount]);

  // Load account groups
  useEffect(() => {
    if (!user) return;
    
    const groupsRef = collection(db, 'users', user.uid, 'account_groups');
    const unsubscribe = onSnapshot(groupsRef, (snapshot) => {
      const groups = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(group => group.active === true)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      
      setAccountGroups(groups);
    });
    
    return unsubscribe;
  }, [user]);

  // Load available ledger codes based on selected group
  useEffect(() => {
    if (!user || !group) {
      setLedgerOptions([]);
      return;
    }
    
    const chartofAccRef = collection(db, 'users', user.uid, 'ChartofAcc');
    const unsubscribe = onSnapshot(chartofAccRef, (snapshot) => {
      const allLedgers = [];
      
      // Determine prefix based on group name
      let prefix = 'DEB'; // default
      const groupLower = group.toLowerCase();
      
      if (groupLower.includes('bank')) {
        prefix = 'BNK';
      } else if (groupLower.includes('credit card')) {
        prefix = 'CCA';
      } else if (groupLower.includes('saving')) {
        prefix = 'SAV';
      }
      
      snapshot.forEach(doc => {
        const data = doc.data();
        
        Object.entries(data).forEach(([ledgerCode, isUsed]) => {
          // Only include available codes (false) that match the prefix
          if (isUsed === false && ledgerCode.includes(prefix)) {
            allLedgers.push(ledgerCode);
          }
        });
      });
      
      allLedgers.sort();
      setLedgerOptions(allLedgers);
    });
    
    return unsubscribe;
  }, [user, group]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      alert('Please enter an account name');
      return;
    }
    
    // Validate account name characters
    if (!name.match(/^[a-zA-Z0-9 _-]+$/)) {
      alert('Account name can only contain letters, numbers, spaces, hyphens, and underscores.\n\n' +
            'Special characters like dots (.) and percent signs (%) are not allowed as they cause issues with the system.');
      return;
    }
    
    if (!group) {
      alert('Please select a group');
      return;
    }
    
    if (!ledger) {
      alert('Please select a ledger code');
      return;
    }
    
    setLoading(true);
    
    try {
      const accountData = {
        active: active,
        allowExpense: allowExpense,
        allowIncome: allowIncome,
        allowTransfer: allowTransfer,
        Ledger: ledger,
        files: files
      };
      
      // Only include name and group in add mode
      if (!isEditMode) {
        accountData.name = name.trim();
        accountData.group = group;
      }
      
      // Add liability-specific fields
      if (isLiabilityGroup) {
        if (cutoffDate) {
          accountData.cutoff_date = cutoffDate; // Already in DD format
        }
        if (creditLimit) {
          accountData.credit_limit = parseInt(creditLimit, 10);
        }
      }
      
      if (isEditMode) {
        // Update existing account
        const docRef = doc(db, 'users', user.uid, 'account_setup', editAccount.id);
        await updateDoc(docRef, accountData);
        alert('Account updated successfully!');
      } else {
        // Add new account
        await addDoc(collection(db, 'users', user.uid, 'account_setup'), accountData);
        
        // Set initial balance if not zero
        const balanceValue = parseFloat(balance) || 0;
        if (balanceValue !== 0) {
          const balancesRef = doc(db, 'users', user.uid, 'balances', 'balances');
          const balanceData = {
            [name.trim()]: balanceValue
          };
          
          // Use setDoc with merge to add/update this account's balance
          const { setDoc } = await import('firebase/firestore');
          await setDoc(balancesRef, balanceData, { merge: true });
        }
        
        alert('Account added successfully!');
      }
      
      // Reset form
      setName('');
      setActive(true);
      setAllowExpense(false);
      setAllowIncome(false);
      setAllowTransfer(false);
      setBalance('0.00');
      setGroup('');
      setCutoffDate('');
      setCreditLimit('');
      setFiles(false);
      setLedger('');
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving account:', error);
      alert('Failed to save account: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>{isEditMode ? 'Edit Account' : 'Add Account'}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="dialog-body">
            
            {/* Account Name */}
            <div className="form-field">
              <label>Account Name *</label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter account name"
                required
                disabled={isEditMode}
              />
            </div>
            
            {/* Group */}
            <div className="form-field">
              <label>Group *</label>
              <select
                className="form-select"
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                required
                disabled={isEditMode}
              >
                <option value="">Select a group</option>
                {accountGroups.map(g => (
                  <option key={g.id} value={g.name}>
                    {g.name} ({g.type})
                  </option>
                ))}
              </select>
            </div>
            
            {/* Ledger Code */}
            <div className="form-field">
              <label>Ledger Code *</label>
              <select
                className="form-select"
                value={ledger}
                onChange={(e) => setLedger(e.target.value)}
                required
              >
                <option value="">Select a ledger code</option>
                {ledgerOptions.map(code => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Initial Balance - Only in Add mode */}
            {!isEditMode && (
              <div className="form-field">
                <label>Initial Balance</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            )}
            
            {/* Cutoff Date - Only for Liability */}
            {isLiabilityGroup && (
              <div className="form-field">
                <label>Cutoff Date (Day of Month)</label>
                <select
                  className="form-select"
                  value={cutoffDate}
                  onChange={(e) => setCutoffDate(e.target.value)}
                >
                  <option value="">Select day</option>
                  {Array.from({ length: 31 }, (_, i) => {
                    const day = (i + 1).toString().padStart(2, '0');
                    return <option key={day} value={day}>{day}</option>;
                  })}
                </select>
              </div>
            )}
            
            {/* Credit Limit - Only for Liability */}
            {isLiabilityGroup && (
              <div className="form-field">
                <label>Credit Limit (whole number)</label>
                <input
                  type="number"
                  step="1"
                  className="form-input"
                  value={creditLimit}
                  onChange={(e) => setCreditLimit(e.target.value)}
                  placeholder="0"
                />
              </div>
            )}
            
            {/* Checkboxes */}
            <div className="form-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>Active</span>
              </label>
            </div>
            
            <div className="form-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={allowIncome}
                  onChange={(e) => setAllowIncome(e.target.checked)}
                />
                <span>Allow Income</span>
              </label>
            </div>
            
            <div className="form-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={allowExpense}
                  onChange={(e) => setAllowExpense(e.target.checked)}
                />
                <span>Allow Expense</span>
              </label>
            </div>
            
            <div className="form-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={allowTransfer}
                  onChange={(e) => setAllowTransfer(e.target.checked)}
                />
                <span>Allow Transfer</span>
              </label>
            </div>
            
            <div className="form-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={files}
                  onChange={(e) => setFiles(e.target.checked)}
                />
                <span>Enable File Uploads</span>
              </label>
            </div>
            
          </div>
          
          <div className="dialog-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Account' : 'Add Account')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddAccountDialog;